<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import NavBar from '@/Components/Layout/Navbar.vue'
import Modal from '@/Components/UI/Modal.vue';
import AuthIdle from './AuthIdle.vue';
import appApi from "@/services/api/global/app"
import employeeApi from "@/services/api/settings/employees/employees"
import calendarApi from "@/services/api/calendar/calendar"
import { useIdleStore } from "@/stores/idle"
import { useUserStore } from '@/stores/user'
import { useLayoutStore } from '@/stores/layout'
import { useSchedulerStore } from '@/stores/scheduler.js'
import { getLocation } from '@/utils/geoLocation';
import { showNotification } from "@/utils/notifications"
import { subscribeToCustomerCenterNotifications, unsubscribeFromCustomerCenterNotifications } from '../services/broadcasting/notificationsCustomerCenterSubscription';
import { subscribeToCustomerNotifications, unsubscribeFromCustomerNotifications } from '../services/broadcasting/notificationsCustomerSubscription';
import { subscribeToCustomerCompanyNotifications, unsubscribeFromCustomerCompanyNotifications } from '../services/broadcasting/notificationsCustomerCompanySubscription';
import { customNotif } from '../utils/notifications';
import { useNotificationsStore } from '@/stores/notifications'; // Asegúrate de que la ruta sea correcta

import { useI18n } from "vue-i18n"

const { t } = useI18n();

const idleStore = useIdleStore()
const userStore = useUserStore()
const layoutStore = useLayoutStore()
const schedulerStore = useSchedulerStore()
const notificationsStore = useNotificationsStore();

const menuLoaded = ref(false);
const sessionClosedByInactivity = ref(0)

let notificationsCenterCustomerBroadcastChannel = null;
let notificationsCustomerBroadcastChannel = null;
let notificationsCompanyCustomerBroadcastChannel = null;

const onActivity = () => { idleStore.touchActivity(); };

const onVisibilityChange = () => {
    if (!document.hidden) idleStore.checkIdleNow();
};

const onWindowFocus = () => { idleStore.checkIdleNow(); };

onMounted(async () => {

    const activityEvents = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"];
    activityEvents.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onWindowFocus);

    // Verifica si el menú ya está cargado en el store
    if (!layoutStore.mainMenu) {

        layoutStore.isLoading = true

        try {
            // -------------------------------------------------------------
            // Peticiones CRÍTICAS: bloquean el render del shell porque
            // alimentan el menu, recursos (usados por copy/paste del
            // calendario), el watcher de inactividad y el modo de layout.
            // getLayout ahora devuelve ademas los settings del shell
            // (SETTINGS_INACTIVITY_TIME, SETTINGS_APPLICATION_LAYOUT_MODE
            // y SETTINGS_NOTIFICATIONS_GLOBAL), eliminando roundtrips
            // adicionales del arranque.
            // -------------------------------------------------------------
            const [
                responseLayout,
                resourcesItems,
            ] = await Promise.all([
                appApi.getLayout(),
                calendarApi.getResources(), //no eliminar, se mete en el store para luego poder copiar y pegar
            ]);

            // Retro-compatibilidad: si el backend todavia devuelve el array
            // de menu directamente (formato antiguo), `responseLayout` es el
            // propio array; si ya devuelve {menu, shell_settings}, leemos de
            // ahi.
            const menu = Array.isArray(responseLayout) ? responseLayout : responseLayout?.menu;
            const shellSettings = Array.isArray(responseLayout) ? {} : (responseLayout?.shell_settings ?? {});

            layoutStore.setMainMenu(Object.freeze(menu))
            schedulerStore.setResourcesItems(resourcesItems.data)

            // Persistimos los shell_settings en userStore para que codigo
            // no-Vue (servicios de broadcasting) pueda leerlos sin recargar
            // del API. Imprescindible que pase ANTES de las suscripciones
            // Pusher para que la subscription pueda decidir el canal
            // (per-center vs notifications-{code}) en funcion de
            // SETTINGS_NOTIFICATIONS_GLOBAL.
            userStore.setShellSettings(shellSettings);

            const layoutModeValue = shellSettings.SETTINGS_APPLICATION_LAYOUT_MODE;
            if (layoutModeValue != null && layoutModeValue !== '') {
                layoutStore.setLayoutMode(layoutModeValue);
            }

            const idleMinutesRaw = shellSettings.SETTINGS_INACTIVITY_TIME;
            if (idleMinutesRaw != null && idleMinutesRaw !== '') {
                const idleMinutes = parseInt(idleMinutesRaw);
                idleStore.setIdleTime((isNaN(idleMinutes) ? 10 : idleMinutes) * 60 * 1000);
            } else {
                idleStore.setIdleTime(10 * 60 * 1000);
            }

            idleStore.startWatching();

            menuLoaded.value = true; // Indica que el menú ya está cargado

            // -------------------------------------------------------------
            // Peticiones NO críticas: ya no se disparan desde aqui, ni
            // siquiera como fire-and-forget. Se cargan bajo demanda en el
            // primer consumidor real:
            //   - countries / business_types / timezones -> formularios de
            //     customer / center / entity / company (lazy-load en los
            //     propios componentes a traves de layoutStore.ensureXxx).
            //   - attendance-status -> Navbar lo pide en su onMounted.
            //   - all whatsapp instances -> se piden al abrir el modal de
            //     WhatsApp.
            //   - sophia tokens -> se piden al abrir ChatAssistantModal.
            // Esto libera el waterfall del arranque de 6 peticiones que no
            // eran necesarias para pintar el calendario ni el shell.
            // -------------------------------------------------------------

        } catch (error) {
            console.error("Error al cargar las opciones del menú:", error);
            menuLoaded.value = true;
        } finally {
            layoutStore.isLoading = false;
        }
    } else {
        // Camino con cache (pinia persist): mainMenu y shellSettings ya
        // estan rehidratados en el store desde localStorage, asi que
        // podemos suscribirnos a Pusher sin esperar al getLayout.
        idleStore.startWatching();
        menuLoaded.value = true;
    }

    // Suscripcion a notificaciones generales del cliente.
    notificationsCustomerBroadcastChannel = subscribeToCustomerNotifications(
        (generalNotificationEvent) => {
            console.log('General notification:', generalNotificationEvent);
            // Mostrar notificación
            customNotif(generalNotificationEvent.title, generalNotificationEvent.message);
            // Agregar al store
            notificationsStore.addNotification(generalNotificationEvent);
        }
    );

    // Suscripcion al canal de notificaciones del centro o tenant-wide
    // (decidido dentro de subscribeToCustomerCenterNotifications a partir
    // de userStore.shellSettings.SETTINGS_NOTIFICATIONS_GLOBAL).
    notificationsCenterCustomerBroadcastChannel = subscribeToCustomerCenterNotifications(
        (whatsappEvent) => {
            customNotif("WhatsApp desconectado", "Una instancia de WhatsApp se ha desconectado.");
            notificationsStore.addNotification(whatsappEvent);
        },
        (botAppointmentCreatedEvent) => {
            const cita = botAppointmentCreatedEvent.appointment || botAppointmentCreatedEvent;
            // Modo de recursos (si viene): employee_only | space_only | both
            const mode = cita?.metaData?.resource_mode;
            const msgKey = mode === 'space_only'
                ? 'app.notifications.appointmentCreated.message.spaceOnly'
                : mode === 'both'
                    ? 'app.notifications.appointmentCreated.message.both'
                    : 'app.notifications.appointmentCreated.message';

            customNotif(
                t('app.notifications.appointmentCreated.subject'),
                t(msgKey, {
                    customer: cita.customer,
                    date: cita.date,
                    startHour: cita.startHour,
                    mainService: cita.mainService,
                    employeeName: cita.employeeName || cita.Subject || ''
                })
            );
            notificationsStore.addNotification(botAppointmentCreatedEvent);
        },
        (botAppointmentCancelledEvent) => {
            const cita = botAppointmentCancelledEvent.appointment || botAppointmentCancelledEvent;
            const mode = cita?.metaData?.resource_mode;
            const msgKey = mode === 'space_only'
                ? 'app.notifications.appointmentCancelled.message.spaceOnly'
                : mode === 'both'
                    ? 'app.notifications.appointmentCancelled.message.both'
                    : 'app.notifications.appointmentCancelled.message';

            customNotif(
                t('app.notifications.appointmentCancelled.subject'),
                t(msgKey, {
                    customer: cita.customer,
                    date: cita.date,
                    startHour: cita.startHour,
                    mainService: cita.mainService,
                    employeeName: cita.employeeName || cita.Subject || ''
                })
            );
            notificationsStore.addNotification(botAppointmentCancelledEvent);
        },
    );

    // Suscripción a notificaciones de empresa (para alertas de stock, etc.)
    notificationsCompanyCustomerBroadcastChannel = subscribeToCustomerCompanyNotifications(
        (generalNotificationEvent) => {
            console.log('General notification (company):', generalNotificationEvent);
            // Mostrar notificación
            customNotif(generalNotificationEvent.title, generalNotificationEvent.message);
            // Agregar al store
            notificationsStore.addNotification(generalNotificationEvent);
        }
    );

});


const closeOpenHeadlessUIComponents = () => {
    document.querySelectorAll('[data-headlessui-state="open"]').forEach(el => {
        const closeBtn = el.querySelector('button[data-headlessui-state]');
        if (closeBtn) closeBtn.click();
    });
    document.activeElement?.blur();
};

watch(() => idleStore.sessionExpired, (newValue) => {
    if (newValue) {
        closeOpenHeadlessUIComponents();
    }
    sessionClosedByInactivity.value = newValue ? 1 : 0;
});

const focusIdlePasswordInput = () => {
    const el = document.getElementById('idle-password');
    if (!el) return;

    document.activeElement?.blur();

    let attempts = 0;
    const tryFocus = () => {
        if (attempts >= 50) return;
        attempts++;
        el.focus();
        if (document.activeElement !== el) {
            requestAnimationFrame(tryFocus);
        }
    };
    requestAnimationFrame(tryFocus);
};

onUnmounted(() => {
    const activityEvents = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"];
    activityEvents.forEach(e => window.removeEventListener(e, onActivity));
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("focus", onWindowFocus);
    idleStore.stopWatching();

    unsubscribeFromCustomerCenterNotifications(notificationsCenterCustomerBroadcastChannel);
    unsubscribeFromCustomerNotifications(notificationsCustomerBroadcastChannel);
    unsubscribeFromCustomerCompanyNotifications(notificationsCompanyCustomerBroadcastChannel);
});

const handleAttendanceChange = async () => {

    if (layoutStore.isLoading) return;
    layoutStore.isLoading = true;

    try {
        // Asegurar estado de fichaje real antes de decidir entrada/salida (el
        // fetch ahora es lazy desde Navbar; el usuario podria pulsar antes de
        // que llegue la primera respuesta).
        await userStore.ensureAttendanceStatus();

        const result = await getLocation();

        if (typeof result === 'number') {
            if (result === 0) {
                showNotification({ type: 'error', message: t('profile.clockIn.locationNotAllowed.info') });
            } else if (result === 1) {
                showNotification({ type: 'error', message: t('profile.clockIn.browserLocationNotAllowed.info') });
            }
            return;
        }

        const location = `${result.latitude},${result.longitude}`;
        const payload = { location };

        if (!userStore.attendanceStatus) {
            await employeeApi.clockIn(userStore.profile.id, payload);
            userStore.setAttendanceStatus(1);
        } else {
            await employeeApi.clockOut(userStore.profile.id, payload);
            userStore.setAttendanceStatus(0);
        }
        showNotification({ type: 'success', message: t('profile.attendanceStatus.ok') });
    } catch (error) {
        console.error('Error en la geolocalización o fichaje:', error);
        const errorMsg = error.response?.data?.errors ?? error.response?.data?.message ?? t('profile.attendanceStatus.error');
        showNotification({ type: 'error', message: errorMsg });
    } finally {
        layoutStore.isLoading = false;
    }
};

</script>
<template>
    <div class="h-full overflow-hidden bg-blue-50" v-if="menuLoaded">
        <NavBar :menuOptions="layoutStore.mainMenu" @attendanceChanged="handleAttendanceChange" />
        <main class="h-full">
            <div class="w-full h-full">
                <router-view />
            </div>
        </main>

        <!-- CUSTOMER INFO REQUEST -->
        <Modal :title="$t('authIdle.modal.title')" :show="sessionClosedByInactivity" :showCloseButton="false"
            :backdropOpacity="90" :backdropBlur="'5px'" @onClosed="" width="max-w-xl"
            @onAfterEnter="focusIdlePasswordInput">
            <AuthIdle />
        </Modal>
    </div>
</template>